import { useEffect, useRef, useState } from "react";

import { useRpcRequest } from "../../hooks/useRpcRequest";

import type { Address, Hex } from "viem";

const DEBOUNCE_MS = 300;

export function useGasEstimation(
  from: Address | null,
  to: Address | null,
  data: Hex | null,
  value?: bigint,
): { gasEstimate: bigint | null; estimating: boolean } {
  const { request } = useRpcRequest();
  const [gasEstimate, setGasEstimate] = useState<bigint | null>(null);
  const [estimating, setEstimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!from || !to || !data) {
      setGasEstimate(null);
      return;
    }

    clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      void (async () => {
        setEstimating(true);
        try {
          const params: Record<string, unknown> = { from, to, data };
          if (value && value > 0n) {
            params.value = `0x${value.toString(16)}`;
          }
          const result = await request("eth_estimateGas", [params]);
          setGasEstimate(BigInt(result as string));
        } catch {
          setGasEstimate(null);
        } finally {
          setEstimating(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [from, to, data, value, request]);

  return { gasEstimate, estimating };
}
